import { useForumType } from '@/components/hooks/useForumType';
import React, { useMemo } from 'react';
import { getResponseCounts, parseUnsafeUrl, postGetAnswerCountStr, postGetCommentCountStr } from '../../../lib/collections/posts/helpers';
import { AnalyticsContext } from "../../../lib/analyticsEvents";
import { extractVersionsFromSemver } from '../../../lib/editor/utils';
import classNames from 'classnames';
import { isFriendlyUI } from '../../../themes/forumTheme';
import type { AnnualReviewMarketInfo } from '../../../lib/collections/posts/annualReviewMarkets';
import PostsPageTitle from "./PostsPageTitle";
import PostsAuthors from "./PostsAuthors";
import LWTooltip from "../../common/LWTooltip";
import PostsPageDate from "./PostsPageDate";
import CrosspostHeaderIcon from "./CrosspostHeaderIcon";
import PostActionsButton from "../../dropdowns/posts/PostActionsButton";
import PostsVote from "../../votes/PostsVote";
import PostsGroupDetails from "../PostsGroupDetails";
import PostsTopSequencesNav from "./PostsTopSequencesNav";
import PostsPageEventData from "./PostsPageEventData";
import FooterTagList from "../../tagging/FooterTagList";
import AddToCalendarButton from "../AddToCalendar/AddToCalendarButton";
import BookmarkButton from "../BookmarkButton";
import ForumIcon from "../../common/ForumIcon";
import GroupLinks from "../../localGroups/GroupLinks";
import SharePostButton from "../SharePostButton";
import AudioToggle from "./AudioToggle";
import ReadTime from "./ReadTime";
import { CommentsLink } from './CommentsLink';
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';

const SECONDARY_SPACING = 20;

const styles = defineStyles('PostsPagePostHeader', (theme: ThemeType) => ({
  header: {
    position: 'relative',
    display:"flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  headerLeft: {
    width: "100%"
  },
  headerVote: {
    textAlign: 'center',
    fontSize: 42,
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      position: 'relative',
      top: 'auto',
      left: 'auto'
    }
  },
  eventHeader: {
    marginBottom: 0,
  },
  authorAndSecondaryInfo: {
    display: 'flex',
    alignItems: 'baseline',
    columnGap: SECONDARY_SPACING,
    flexWrap: 'wrap',
    fontSize: '1.4rem',
    fontFamily: theme.typography.uiSecondary.fontFamily,
    color: theme.palette.text.dim3
  },
  secondaryInfo: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    columnGap: SECONDARY_SPACING,
    rowGap: '10px',
    flexWrap: 'wrap',
    [theme.breakpoints.down("sm")]: {
      justifyContent: 'flex-start'
    }
  },
  secondaryInfoLeft: {
    display: 'flex',
    alignItems: 'baseline',
    columnGap: SECONDARY_SPACING,
    flexWrap: 'wrap'
  },
  secondaryInfoRight: {
    flex: 'none',
    display: 'flex',
    columnGap: SECONDARY_SPACING
  },
  secondaryInfoLink: {
    fontSize: theme.typography.body2.fontSize,
    "@media print": { display: "none" },
  },
  actions: {
    color: theme.palette.grey[500],
    "&:hover": {
      opacity: 0.5,
    },
    '& svg': {
      color: 'inherit' // this is needed for the EAF version of the icon
    },
    "@media print": { display: "none" },
  },
  authorInfo: {
    display: 'flex',
    alignItems: 'baseline',
    columnGap: SECONDARY_SPACING,
  },
  authors: {
    fontSize: theme.typography.body1.fontSize,
  },
  feedName: {
    fontSize: theme.typography.body2.fontSize,
    [theme.breakpoints.down('sm')]: {
      display: "none"
    }
  },
  divider: {
    marginTop: 16,
    marginLeft:0,
    borderTop: theme.palette.border.faint,
    borderLeft: 'transparent'
  },
  commentIcon: {
    fontSize: "1.4em",
    marginRight: 1,
    transform: "translateY(5px)",
  },
  bookmarkButton: {
    marginBottom: -5,
    height: 22,
    color: theme.palette.grey[600],
    "&:hover": {
      opacity: 0.5,
    },
  },
  headerFooter: { 
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    marginTop: 8,
    marginBottom: 16,
  },
  tagSection: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    height: "100%",
  }
}));

/// PostsPagePostHeader: The metadata block at the top of a post page, with
/// title, author, voting, an actions menu, etc.
const PostsPagePostHeader = ({post, answers = [], dialogueResponses = [], showEmbeddedPlayer, toggleEmbeddedPlayer, hideMenu, hideTags, annualReviewMarketInfo}: {
  post: PostsWithNavigation|PostsWithNavigationAndRevision|PostsListWithVotes,
  answers?: CommentsList[],
  dialogueResponses?: readonly CommentsList[],
  showEmbeddedPlayer?: boolean,
  toggleEmbeddedPlayer?: () => void,
  hideMenu?: boolean,
  hideTags?: boolean,
  annualReviewMarketInfo?: AnnualReviewMarketInfo,
}) => {
  const { forumType } = useForumType();
  const classes = useStyles(styles);
  const hasMajorRevision = ('version' in post) && extractVersionsFromSemver(post.version).major > 1
  const rssFeedSource = ('feed' in post) ? post.feed : null;
  let feedLinkDomain;
  let feedLink;
  if (rssFeedSource?.url) {
    let feedLinkProtocol;
    ({ hostname: feedLinkDomain, protocol: feedLinkProtocol } = parseUnsafeUrl(rssFeedSource.url));
    feedLink = `${feedLinkProtocol}//${feedLinkDomain}`;
  }
  const crosspostNode = post.fmCrosspost?.isCrosspost && !post.fmCrosspost.hostedHere &&
    <CrosspostHeaderIcon post={post} />

  const {
    answerCount,
    commentCount,
  } = useMemo(() => getResponseCounts({ post, answers, forumType }), [post, answers, forumType]);

  const minimalSecondaryInfo = post.isEvent || (isFriendlyUI() && post.shortform);

  const answersNode = !post.question || minimalSecondaryInfo
    ? null
    : (
      <CommentsLink anchor="#answers" className={classes.secondaryInfoLink}>
        {postGetAnswerCountStr(answerCount)}
      </CommentsLink>
    );

  const addToCalendarNode = post.startTime && <div className={classes.secondaryInfoLink}>
    <AddToCalendarButton post={post} label="Add to calendar" hideTooltip />
  </div>

  const tripleDotMenuNode = !hideMenu &&
    <span className={classes.actions}>
      <AnalyticsContext pageElementContext="tripleDotMenu">
        <PostActionsButton post={post} includeBookmark flip={true}/>
      </AnalyticsContext>
    </span>

  // EA Forum splits the info into two sections, plus has the info in a different order
  const secondaryInfoNode = <div className={classes.secondaryInfo}>
      <div className={classes.secondaryInfoLeft}>
        {!minimalSecondaryInfo &&
          <>
            <PostsPageDate post={post} hasMajorRevision={hasMajorRevision} />
            <ReadTime post={post} dialogueResponses={dialogueResponses} />
          </>
        }
        <AudioToggle post={post} toggleEmbeddedPlayer={toggleEmbeddedPlayer} showEmbeddedPlayer={showEmbeddedPlayer} />
        {post.isEvent && <GroupLinks document={post} noMargin />}
        {answersNode}
        {!post.shortform &&
          <LWTooltip title={postGetCommentCountStr(post, forumType, commentCount)}>
            <CommentsLink anchor="#comments" className={classes.secondaryInfoLink}>
              <ForumIcon icon="Comment" className={classes.commentIcon} /> {commentCount}
            </CommentsLink>
          </LWTooltip>
        }
        {addToCalendarNode}
        {crosspostNode}
      </div>
      <div className={classes.secondaryInfoRight}>
        <BookmarkButton documentId={post._id} collectionName="Posts" initial={post.isBookmarked} className={classes.bookmarkButton} placement='bottom-start' />
        <SharePostButton post={post} />
        {tripleDotMenuNode}
      </div>
    </div>

  // TODO: If we are not the primary author of this post, but it was shared with
  // us as a draft, display a notice and a link to the collaborative editor.

  return <>
    {post.group && <PostsGroupDetails post={post} documentId={post.group._id} />}
    <AnalyticsContext pageSectionContext="topSequenceNavigation">
      {('sequence' in post) && <PostsTopSequencesNav post={post} />}
    </AnalyticsContext>
    <div className={classNames(classes.header, {[classes.eventHeader]: post.isEvent})}>
      <div className={classes.headerLeft}>
        <PostsPageTitle post={post} />
        <div className={classes.authorAndSecondaryInfo}>
          <div className={classes.authorInfo}>
            <div className={classes.authors}>
              <PostsAuthors post={post} pageSectionContext="post_header" />
            </div>
            {rssFeedSource && rssFeedSource.user &&
              <LWTooltip title={`Crossposted from ${feedLinkDomain}`} className={classes.feedName}>
                <a href={feedLink}>{rssFeedSource.nickname}</a>
              </LWTooltip>
            }
          </div>
          {secondaryInfoNode}
        </div>
      </div>
      {!post.shortform && <div className={classes.headerVote}>
        <PostsVote post={post} />
      </div>}
    </div>
    <div className={classes.headerFooter}>
      <div className={classes.tagSection}>
        {!post.shortform && !post.isEvent && !hideTags && 
        <AnalyticsContext pageSectionContext="tagHeader">
          <FooterTagList post={post} hideScore allowTruncate overrideMargins={true} annualReviewMarketInfo={annualReviewMarketInfo} />
        </AnalyticsContext>}
      </div>
    </div>
    {post.isEvent && <PostsPageEventData post={post}/>}
  </>
}

export default PostsPagePostHeader;

